/**
 * SequenceUpdater — create / edit an email sequence (side drawer, opened from SequencesManager).
 *
 * Reads top to bottom like a sentence:
 *   1. מי נכנס לרצף     trigger tags — customers who GET one of these tags enter
 *   2. מה הם מקבלים     ordered emails: template + delay (number + unit, stored as delayMinutes;
 *                       step 1 counts from entering, the others from the previous email)
 *   3. פרטים            name, description, "רצף שירות" (isTransactional)
 *
 * Everything is a local draft — nothing reaches the server until a save button:
 *   draft / paused sequence → "שמור כטיוטה" (or "שמור") · "שמור והפעל"
 *   active sequence         → "שמור"
 * One request saves it all (new API, services/service.emailSequences.js):
 *   POST /api/email/sequences { name, description, isTransactional, steps, tagIds, activate?, includeExisting? }
 *   PUT  /api/email/sequences/:id { …, steps (full ordered list, existing ones keep `id`), tagIds, includeExisting? }
 *   POST /api/email/sequences/:id/activate { includeExisting }   (save-and-activate of an existing sequence)
 *
 * The admin always sees what will happen before it happens:
 *   - live, while editing (debounced POST /api/email/sequences/impact on the draft): how many customers
 *     already have the chosen tags and how many of them can get it, who stops when a tag is removed
 *     from an active sequence, duplicate name, sending limits; per saved email, how many wait for it
 *     (sequence.steps[].waiting)
 *   - before every save: useImpactConfirm shows the impact (stopped customers, what step edits do to
 *     customers mid-sequence, transactional changes, turning on) and asks the "existing customers"
 *     question when people would enter now. Plain saves with nothing to tell skip the dialog.
 *
 * Props: sequence (from the list; null = new), onClose(refresh)
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import {
  ArrowUpOutlined,
  ArrowDownOutlined,
  DeleteOutlined,
  PlusOutlined,
  InfoCircleOutlined,
  ExclamationCircleOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import * as actionsCreators from './../../../actions/index.js';
import {
  EditDrawer,
  Section,
  Field,
  Button,
  Input,
  InputNumber,
  Select,
  Tooltip,
  Checkbox,
  StatusBadge,
  EmptyState,
  LIST,
  useLookupList,
} from '../../../ui';
import useImpactConfirm from './useImpactConfirm';
import {
  SEQUENCE_STATUS,
  statusOf,
  statusExplanation,
  UNITS,
  splitDelay,
  toMinutes,
  delayLabel,
  sequenceErrorText,
  excludedText,
  sendingLimitsText,
} from './sequenceHelpers';
import classes from './SequenceUpdater.css';

let keySeq = 0;
const newKey = () => `k${++keySeq}`;

// Server sequence → editable draft
const toDraft = (sequence) => ({
  name: (sequence && sequence.name) || '',
  description: (sequence && sequence.description) || '',
  isTransactional: !!(sequence && sequence.isTransactional),
  tagIds: sequence ? sequence.tagIds || (sequence.triggerTags || []).map((t) => t.tag_id) : [],
  steps: sequence
    ? (sequence.steps || [])
        .slice()
        .sort((a, b) => a.stepOrder - b.stepOrder)
        .map((s) => ({ key: newKey(), id: s.id, template_id: s.template_id, ...splitDelay(s.delayMinutes) }))
    : [],
});

// Comparable snapshot for the unsaved-changes check
const snapshot = (d) =>
  JSON.stringify({ ...d, steps: d.steps.map((s) => [s.id, s.template_id, toMinutes(s.amount, s.unit)]) });

const LIVE_DELAY_MS = 400;

const SequenceUpdater = (props) => {
  const { sequence } = props;
  const isNew = !sequence;
  const status = isNew ? 'draft' : statusOf(sequence);
  const isActive = status === 'active';

  const [open, setOpen] = useState(true);
  const refreshRef = useRef(false);
  const initial = useMemo(() => toDraft(sequence), [sequence]);
  const [draft, setDraft] = useState(initial);
  const [templates, setTemplates] = useState([]);
  const [saving, setSaving] = useState(null); // 'draft' | 'activate' | 'save' while a request runs
  const [live, setLive] = useState(null); // impact of the current draft, for the live hints
  const liveRequest = useRef(0);
  const tags = useLookupList(LIST.TAG);

  const [confirmImpact, impactDialog] = useImpactConfirm({
    getImpact: (body) => props.getSequenceImpact(body),
    showAlert: props.showAlert,
  });

  useEffect(() => {
    props
      .getEmailTemplates()
      .then(setTemplates)
      .catch((e) => console.error('Error loading templates:', e));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dirty = snapshot(draft) !== snapshot(initial);
  const set = (changes) => setDraft((d) => ({ ...d, ...changes }));
  const setStep = (key, changes) => setDraft((d) => ({ ...d, steps: d.steps.map((s) => (s.key === key ? { ...s, ...changes } : s)) }));

  const close = (refresh) => {
    refreshRef.current = !!refresh;
    setOpen(false);
  };

  // ---- steps ------------------------------------------------------------------
  const addStep = () =>
    setDraft((d) => ({
      ...d,
      steps: [...d.steps, { key: newKey(), template_id: undefined, amount: d.steps.length ? 1 : 0, unit: 1440 }],
    }));
  const removeStep = (key) => setDraft((d) => ({ ...d, steps: d.steps.filter((s) => s.key !== key) }));
  const moveStep = (index, dir) =>
    setDraft((d) => {
      const steps = d.steps.slice();
      const j = index + dir;
      if (j < 0 || j >= steps.length) return d;
      [steps[index], steps[j]] = [steps[j], steps[index]];
      return { ...d, steps };
    });

  // ---- save ---------------------------------------------------------------------
  const validate = () => {
    if (!draft.name.trim()) {
      props.showAlert('נא למלא שם רצף');
      return false;
    }
    const missing = draft.steps.findIndex((s) => !s.template_id);
    if (missing !== -1) {
      props.showAlert(`נא לבחור תבנית למייל ${missing + 1}`);
      return false;
    }
    return true;
  };

  const payload = () => ({
    name: draft.name.trim(),
    description: draft.description,
    isTransactional: draft.isTransactional,
    tagIds: draft.tagIds,
    steps: draft.steps.map((s) => ({ ...(s.id ? { id: s.id } : {}), template_id: s.template_id, delayMinutes: toMinutes(s.amount, s.unit) })),
  });

  // Impact request for saving the current draft (activate: 'save and turn on')
  const impactRequest = (activate) => ({
    action: isNew ? 'create' : 'save',
    ...(isNew ? {} : { sequenceId: sequence.id }),
    draft: { ...payload(), ...(activate ? { activate: true } : {}) },
  });

  // ---- live hints: impact of the draft, debounced; steps without a template are left out ----
  const liveKey = JSON.stringify([draft.name.trim(), draft.isTransactional, draft.tagIds, draft.steps.map((s) => [s.id, s.template_id, toMinutes(s.amount, s.unit)])]);
  useEffect(() => {
    const id = ++liveRequest.current;
    const timer = setTimeout(() => {
      const request = impactRequest(false);
      request.draft.steps = request.draft.steps.filter((s) => s.template_id);
      props
        .getSequenceImpact(request)
        .then((impact) => {
          if (id === liveRequest.current) setLive(impact);
        })
        .catch((e) => console.error('live impact failed:', e));
    }, LIVE_DELAY_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveKey]);

  const resultText = (res, base) => {
    const parts = [base];
    if (res && res.enrolledExisting > 0) parts.push(`${res.enrolledExisting} לקוחות נוספו לרצף`);
    if (res && res.stoppedEnrollments > 0) parts.push(`${res.stoppedEnrollments} לקוחות הוצאו מהרצף`);
    return parts.join(' · ');
  };

  const run = async (kind, fn) => {
    setSaving(kind);
    try {
      await fn();
    } catch (error) {
      console.error('Error saving sequence:', error);
      props.showAlert(sequenceErrorText(error));
    }
    setSaving(null);
  };

  // "שמור כטיוטה" / "שמור" for a draft or paused sequence — never lets anyone in
  const saveDraft = async () => {
    if (!validate()) return;
    setSaving('draft');
    const answer = await confirmImpact(impactRequest(false), { title: 'שמירת השינויים', okText: 'שמור', skipIfNothingToSay: true, onLoaded: () => setSaving(null) });
    if (!answer) return;
    run('draft', async () => {
      const res = isNew
        ? await props.createEmailSequence({ ...payload(), activate: false })
        : await props.updateEmailSequence(sequence.id, payload());
      props.showAlert(resultText(res, 'הרצף נשמר'));
      close(true);
    });
  };

  // "שמור והפעל" — shows everything turning on will do, and asks about existing customers
  const saveAndActivate = async () => {
    if (!validate()) return;
    setSaving('activate');
    const answer = await confirmImpact(impactRequest(true), {
      title: isNew ? 'שמירה והפעלה של הרצף' : `הפעלת הרצף "${draft.name.trim()}"`,
      okText: 'שמור והפעל',
      onLoaded: () => setSaving(null),
    });
    if (!answer) return;
    run('activate', async () => {
      let res;
      if (isNew) {
        res = await props.createEmailSequence({ ...payload(), activate: true, includeExisting: answer.includeExisting });
      } else {
        const saved = await props.updateEmailSequence(sequence.id, payload());
        res = { ...(await props.activateEmailSequence(sequence.id, answer.includeExisting)), stoppedEnrollments: saved && saved.stoppedEnrollments };
      }
      props.showAlert(resultText(res, 'הרצף הופעל'));
      close(true);
    });
  };

  // "שמור" for an active sequence — removed tags stop people, added tags may let existing customers in
  const saveActive = async () => {
    if (!validate()) return;
    setSaving('save');
    const answer = await confirmImpact(impactRequest(false), { title: 'שמירת השינויים', okText: 'שמור', skipIfNothingToSay: true, onLoaded: () => setSaving(null) });
    if (!answer) return;
    run('save', async () => {
      const res = await props.updateEmailSequence(sequence.id, { ...payload(), includeExisting: answer.includeExisting });
      props.showAlert(resultText(res, 'הרצף נשמר'));
      close(true);
    });
  };

  // ---- render -------------------------------------------------------------------
  const noSteps = draft.steps.length === 0;
  const st = SEQUENCE_STATUS[status];
  const counts = (sequence && sequence.enrollmentCounts) || {};
  const inProgress = (counts.active || 0) + (counts.paused || 0);
  const waitingById = new Map(((sequence && sequence.steps) || []).map((s) => [s.id, s.waiting || { active: 0, paused: 0 }]));
  const liveWarning = (code) => !!(live && (live.warnings || []).some((w) => w.code === code));

  // "ל-40 לקוחות כבר יש את הטאגים (lead: 30, quiz: 12). 25 מהם יכולים לקבל את הרצף — …"
  const holdersHint = () => {
    const h = live && live.existingTagHolders;
    if (!h || !h.holders) return null;
    const perTag = h.tags.length > 1 ? ` (${h.tags.map((t) => `${t.name || `#${t.tagId}`}: ${t.holders}`).join(', ')})` : '';
    const which = h.appliesTo === 'added_tags' ? 'את הטאג שהוספתם' : 'את הטאגים';
    const when = isActive ? 'בשמירה' : 'כשתפעילו את הרצף';
    if (!h.eligible) {
      const why = excludedText(h.excluded).replace('לא יקבלו: ', '');
      return `ל-${h.holders} לקוחות כבר יש ${which}${perTag}, אבל אף אחד מהם לא יקבל את הרצף${why ? ` (${why})` : ''}.`;
    }
    return `ל-${h.holders} לקוחות כבר יש ${which}${perTag}. ${h.eligible} מהם יכולים לקבל את הרצף — ${when} תוכלו לבחור אם לשלוח גם להם.`;
  };

  const footer = (requestClose) =>
    isActive ? (
      <>
        <Button onClick={requestClose}>ביטול</Button>
        <Button type="primary" loading={saving === 'save'} onClick={saveActive}>
          שמור
        </Button>
      </>
    ) : (
      <>
        <Button onClick={requestClose}>ביטול</Button>
        <Button loading={saving === 'draft'} onClick={saveDraft}>
          {status === 'paused' ? 'שמור' : 'שמור כטיוטה'}
        </Button>
        <Tooltip title={noSteps ? 'הוסיפו לפחות מייל אחד כדי להפעיל' : null}>
          <Button type="primary" disabled={noSteps} loading={saving === 'activate'} onClick={saveAndActivate}>
            שמור והפעל
          </Button>
        </Tooltip>
      </>
    );

  const templateOptions = templates.map((t) => ({ value: t.id, label: t.name }));
  const holders = holdersHint();

  return (
    <>
      <EditDrawer
        open={open}
        width={640}
        title={isNew ? 'רצף חדש' : sequence.name}
        subtitle={
          isNew ? (
            'נשמר כטיוטה עד שמפעילים אותו. טיוטה לא שולחת מיילים.'
          ) : (
            <Tooltip title={statusExplanation(sequence)}>
              <span>
                <StatusBadge color={st.color}>{st.label}</StatusBadge>
              </span>
            </Tooltip>
          )
        }
        dirty={dirty}
        onClose={() => close(false)}
        afterClose={() => props.onClose(refreshRef.current)}
        footer={footer}
      >
        {/* 1. who enters */}
        <Section title="מי נכנס לרצף">
          <Select
            mode="multiple"
            allowClear
            style={{ width: '100%' }}
            optionFilterProp="label"
            placeholder="בחרו טאגים…"
            loading={tags.loading}
            value={draft.tagIds}
            onChange={(ids) => set({ tagIds: ids })}
            options={tags.items.map((t) => ({ value: t.id, label: t.name || t.val }))}
          />
          <p className={classes.hint}>
            {isActive
              ? 'כל מי שיקבל אחד מהטאגים האלה ייכנס לרצף. מי שכבר יש לו/ה את הטאג נכנס/ת רק אם תבחרו בזה. הסרת טאג מוציאה מהרצף את מי שנכנס דרכו.'
              : 'כשהרצף פעיל, כל מי שיקבל אחד מהטאגים האלה ייכנס אליו. מי שכבר יש לו/ה את הטאג נכנס/ת רק אם תבחרו בזה.'}
          </p>
          {holders ? <p className={classes.live}>{holders}</p> : null}
          {live && live.stop && live.stop.total > 0 ? (
            <p className={classes.liveDanger}>
              <WarningOutlined /> {live.stop.total} לקוחות שבאמצע הרצף נכנסו דרך הטאג שהסרתם — בשמירה הם יפסיקו לקבל מיילים מהרצף.
            </p>
          ) : null}
          {liveWarning('NO_TRIGGER_TAGS') ? (
            <p className={classes.liveWarn}>
              <ExclamationCircleOutlined /> בלי טאגים אף אחד לא ייכנס לרצף.
            </p>
          ) : null}
        </Section>

        {/* 2. what they get */}
        <Section title={`מה הם מקבלים${draft.steps.length ? ` (${draft.steps.length} מיילים)` : ''}`}>
          {inProgress > 0 ? (
            <div className={classes.note}>
              <InfoCircleOutlined /> {inProgress} לקוחות נמצאים באמצע הרצף, ושינויים כאן חלים גם עליהם: הם ממשיכים לחכות לאותו מייל
              גם אם הזזתם אותו. לפני השמירה תראו בדיוק מה יקרה להם.
            </div>
          ) : null}

          {noSteps ? (
            <EmptyState compact title="עדיין אין מיילים ברצף" />
          ) : (
            <ol className={classes.steps}>
              {draft.steps.map((s, i) => {
                const waiting = s.id ? waitingById.get(s.id) : null;
                return (
                  <li key={s.key} className={classes.step}>
                    <div className={classes.stepHead}>
                      <span className={classes.stepNumber}>{i + 1}</span>
                      <span className={classes.stepWhen}>{delayLabel(toMinutes(s.amount, s.unit), i === 0)}</span>
                      {waiting && waiting.active + waiting.paused > 0 ? (
                        <Tooltip title="לקוחות שהמייל הבא שלהם הוא המייל הזה">
                          <span className={classes.waiting}>
                            {waiting.active} מחכים למייל הזה{waiting.paused ? ` (+${waiting.paused} מושהים)` : ''}
                          </span>
                        </Tooltip>
                      ) : null}
                      <span className={classes.stepTools}>
                        <Tooltip title="העבר למעלה">
                          <Button type="text" size="small" icon={<ArrowUpOutlined />} disabled={i === 0} onClick={() => moveStep(i, -1)} />
                        </Tooltip>
                        <Tooltip title="העבר למטה">
                          <Button
                            type="text"
                            size="small"
                            icon={<ArrowDownOutlined />}
                            disabled={i === draft.steps.length - 1}
                            onClick={() => moveStep(i, 1)}
                          />
                        </Tooltip>
                        <Tooltip title="מחק מייל">
                          <Button type="text" size="small" danger icon={<DeleteOutlined />} onClick={() => removeStep(s.key)} />
                        </Tooltip>
                      </span>
                    </div>
                    <div className={classes.stepFields}>
                      <Select
                        showSearch
                        optionFilterProp="label"
                        placeholder="בחרו תבנית…"
                        status={s.template_id ? undefined : 'warning'}
                        value={s.template_id}
                        onChange={(v) => setStep(s.key, { template_id: v })}
                        options={templateOptions}
                      />
                      <div className={classes.delay}>
                        <InputNumber min={0} value={s.amount} onChange={(v) => setStep(s.key, { amount: v || 0 })} aria-label="השהייה" />
                        <Select value={s.unit} onChange={(u) => setStep(s.key, { unit: u })} options={UNITS} popupMatchSelectWidth={false} />
                      </div>
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
          <Button block type="dashed" icon={<PlusOutlined />} onClick={addStep} className={classes.addStep}>
            הוסף מייל
          </Button>
          {live && live.sendingLimits ? <p className={classes.limits}>{sendingLimitsText(live.sendingLimits)}</p> : null}
        </Section>

        {/* 3. details */}
        <Section title="פרטים">
          <div className={classes.details}>
            <Field label="שם הרצף" required>
              <Input
                value={draft.name}
                status={liveWarning('DUPLICATE_NAME') ? 'error' : undefined}
                onChange={(e) => set({ name: e.target.value })}
                placeholder="לדוגמה: קבלת פנים ללקוחות חדשים"
              />
              {liveWarning('DUPLICATE_NAME') ? <span className={classes.fieldError}>כבר יש רצף בשם הזה</span> : null}
            </Field>
            <Field label="תיאור">
              <Input.TextArea
                autoSize={{ minRows: 2, maxRows: 4 }}
                value={draft.description}
                onChange={(e) => set({ description: e.target.value })}
                placeholder="תיאור קצר של מטרת הרצף"
              />
            </Field>
            <Checkbox checked={draft.isTransactional} onChange={(e) => set({ isTransactional: e.target.checked })}>
              <span className={classes.checkTitle}>רצף שירות (מתנה / גישה לקורס) — נשלח גם למי שלא אישר/ה דיוור שיווקי</span>
              <span className={classes.checkHint}>
                לסמן רק אם כל המיילים ברצף מוסרים את מה שהלקוח/ה ביקש/ה. תוכן שיווקי/המשך = לא לסמן. לא עוקף הסרה מרשימת
                תפוצה, bounce או תלונת ספאם.
              </span>
            </Checkbox>
          </div>
        </Section>
      </EditDrawer>
      {impactDialog}
    </>
  );
};

const mapStateToProps = () => ({});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      createEmailSequence: actionsCreators.createEmailSequence,
      updateEmailSequence: actionsCreators.updateEmailSequence,
      activateEmailSequence: actionsCreators.activateEmailSequence,
      getSequenceImpact: actionsCreators.getSequenceImpact,
      getEmailTemplates: actionsCreators.getEmailTemplates,
      showAlert: actionsCreators.showAlert,
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(SequenceUpdater);
